import { useEffect, useState, useRef, useCallback } from 'react'

export interface NavigationItem {
  name: string
  href: string
  icon: any // Using any for Lucide icon components
  priority?: number // Lower number = higher priority
}

interface UseResponsiveNavigationProps {
  mainItems: NavigationItem[]
  moreItems: NavigationItem[]
  customPages?: any[]
}

interface UseResponsiveNavigationReturn {
  visibleMainItems: NavigationItem[]
  dropdownItems: NavigationItem[]
  isCalculating: boolean
}

// Debounce function to prevent excessive recalculations
const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export const useResponsiveNavigation = ({
  mainItems,
  moreItems,
  customPages = []
}: UseResponsiveNavigationProps): UseResponsiveNavigationReturn => {
  const [visibleMainItems, setVisibleMainItems] = useState(mainItems)
  const [dropdownItems, setDropdownItems] = useState(moreItems)
  const [isCalculating, setIsCalculating] = useState(true)
  
  const navContainerRef = useRef<HTMLDivElement | null>(null)
  const measurementRef = useRef<HTMLDivElement | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const lastCalculatedWidth = useRef<number>(0)

  // All more items are potentially promotable, already sorted by the parent component
  const promotableItems = moreItems

  const calculateVisibleItems = useCallback(() => {
    if (!navContainerRef.current || !measurementRef.current) {
      // If refs aren't ready, try again shortly
      setTimeout(() => calculateVisibleItems(), 50)
      return
    }

    const container = navContainerRef.current
    const containerWidth = container.offsetWidth
    
    // Skip calculation if width hasn't changed significantly (avoid layout thrashing)
    if (Math.abs(containerWidth - lastCalculatedWidth.current) < 5) {
      return
    }
    
    lastCalculatedWidth.current = containerWidth

    setIsCalculating(true)

    // Get left and right content widths
    const leftContent = container.querySelector('.nav-left-content') as HTMLElement
    const rightContent = container.querySelector('.nav-right-content') as HTMLElement
    
    if (!leftContent || !rightContent) {
      setIsCalculating(false)
      return
    }

    const leftWidth = leftContent.offsetWidth
    const rightWidth = rightContent.offsetWidth
    const moreButtonWidth = 80 // Approximate width for "More" button with padding
    const itemGap = 8 // gap-2 in tailwind
    const containerPadding = 32 // px-4 on each side
    
    // Calculate available space for navigation items
    // We need to account for the More button even if it might not be shown
    const availableWidth = containerWidth - leftWidth - rightWidth - moreButtonWidth - containerPadding - (itemGap * 4)

    // Create a temporary element to measure text widths
    const measureElement = document.createElement('div')
    measureElement.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      font-size: 0.875rem;
      font-weight: 500;
      padding: 0.5rem 0.75rem;
    `
    measurementRef.current.appendChild(measureElement)

    // Measure each item's width
    const measureItemWidth = (item: NavigationItem): number => {
      measureElement.textContent = item.name
      const width = measureElement.offsetWidth
      return width + itemGap
    }

    // Calculate widths for all items
    const mainItemWidths = mainItems.map(item => ({
      item,
      width: measureItemWidth(item)
    }))

    const promotableItemWidths = promotableItems.map(item => ({
      item,
      width: measureItemWidth(item)
    }))

    // Remove measurement element
    measurementRef.current.removeChild(measureElement)

    // Always include main navigation items (up to 5)
    let currentWidth = mainItemWidths.reduce((sum, { width }) => sum + width, 0)

    // Check which promotable items can fit (but limit total to 5 items)
    const newVisibleItems = [...mainItems]
    const newDropdownItems: NavigationItem[] = []

    for (const { item, width } of promotableItemWidths) {
      // Limit to 5 total items in main navigation
      if (currentWidth + width <= availableWidth && newVisibleItems.length < 5) {
        currentWidth += width
        newVisibleItems.push(item)
      } else {
        newDropdownItems.push(item)
      }
    }

    // Update state only if something changed
    const visibleChanged = newVisibleItems.length !== visibleMainItems.length ||
      newVisibleItems.some((item, i) => item.name !== visibleMainItems[i]?.name)
    
    if (visibleChanged) {
      setVisibleMainItems(newVisibleItems)
      setDropdownItems(newDropdownItems)
    }

    setIsCalculating(false)
  }, [mainItems, moreItems, promotableItems, visibleMainItems])

  // Debounced calculation function
  const debouncedCalculate = useCallback(
    debounce(calculateVisibleItems, 150),
    [calculateVisibleItems]
  )

  // Set up refs and ResizeObserver
  useEffect(() => {
    // Create measurement container
    const measurementDiv = document.createElement('div')
    measurementDiv.style.cssText = `
      position: absolute;
      visibility: hidden;
      height: 0;
      overflow: hidden;
      pointer-events: none;
    `
    document.body.appendChild(measurementDiv)
    measurementRef.current = measurementDiv

    // Find navigation container
    const findAndObserveNav = () => {
      const navContainer = document.querySelector('.responsive-nav-container') as HTMLDivElement
      if (navContainer) {
        navContainerRef.current = navContainer

        // Set up ResizeObserver
        resizeObserverRef.current = new ResizeObserver((entries) => {
          // Only recalculate if the width actually changed
          for (const entry of entries) {
            if (entry.contentBoxSize) {
              debouncedCalculate()
            }
          }
        })

        resizeObserverRef.current.observe(navContainer)
        
        // Initial calculation after a short delay to ensure DOM is ready
        setTimeout(calculateVisibleItems, 100)
      } else {
        // If nav container not found, try again
        setTimeout(findAndObserveNav, 50)
      }
    }

    findAndObserveNav()

    // Also listen for window resize as a fallback
    const handleResize = () => debouncedCalculate()
    window.addEventListener('resize', handleResize)

    return () => {
      if (resizeObserverRef.current && navContainerRef.current) {
        resizeObserverRef.current.unobserve(navContainerRef.current)
        resizeObserverRef.current.disconnect()
      }
      if (measurementRef.current && document.body.contains(measurementRef.current)) {
        document.body.removeChild(measurementRef.current)
      }
      window.removeEventListener('resize', handleResize)
    }
  }, [calculateVisibleItems, debouncedCalculate])

  // Recalculate when custom pages change
  useEffect(() => {
    if (!isCalculating) {
      debouncedCalculate()
    }
  }, [customPages?.length, debouncedCalculate, isCalculating])

  // Update state when mainItems or moreItems change
  useEffect(() => {
    setVisibleMainItems(mainItems)
    setDropdownItems(moreItems)
    // Force recalculation when items change
    lastCalculatedWidth.current = 0
    if (!isCalculating) {
      debouncedCalculate()
    }
  }, [mainItems, moreItems, debouncedCalculate, isCalculating])

  return {
    visibleMainItems,
    dropdownItems,
    isCalculating
  }
}